import * as R from 'ramda';
import debug from 'debug';
import { PatchScript } from '@pos-refacto/patchscript-with-react';

import { addError } from 'actions/Error';
import { getProductsUniversal } from 'actions/productsDB';
import { PosPlugin } from 'plugins/plugin';
import { getTgfConfiguration } from 'plugins/TGF/configuration/Configuration';
import { SaleDocumentResponse } from 'types/SalesDocument';

const baseLog = debug('plugin:tgf-customizations');
const log = baseLog.extend('onGenerateReceipt.on');

const emptyTextLine: PatchScript[number] = {
  type: 'text',
  pieces: [
    {
      text: '',
      meta: {
        bold: false,
        underline: false,
      },
    },
  ],
};

function convertTextToPatchscript(text: string): PatchScript[number] {
  return {
    type: 'text',
    align: 'center',
    pieces: [
      {
        text,
        meta: {
          bold: false,
          underline: false,
        },
      },
    ],
  };
}

type OnGenerateReceipt = Required<Required<PosPlugin>['onGenerateReceipt']>;

export function getProductGroupIds(
  salesDocument: Parameters<OnGenerateReceipt['on']>[0]['salesDocument'],
) {
  return async dispatch => {
    log(
      'Checking if salesDocument parameter contains product group',
      salesDocument,
    );

    const rowsWithPositiveAmounts = (salesDocument as SaleDocumentResponse).rows.filter(
      row => row.amount > 0,
    );
    if (!rowsWithPositiveAmounts.length) {
      log('Sale is a return. No need add footers');
      return new Set();
    }

    const productGroupIds = rowsWithPositiveAmounts
      .map(row => row.productGroupID)
      .filter(Boolean)
      .map(String);
    log('Got product group ids', productGroupIds);

    const propsContainAllProductGroupIds =
      (productGroupIds?.length ?? 0) === rowsWithPositiveAmounts.length;
    if (propsContainAllProductGroupIds) {
      log(
        'Found product group ids of all rows in salesDocument parameter. Returning group ids',
        productGroupIds,
      );
      return new Set(productGroupIds);
    }
    log('Did not find product group ids in salesDocument parameter');
    const productIds = rowsWithPositiveAmounts
      .map(row => row.productID)
      .filter(Boolean);
    log('Fetching products from sale', productIds);
    const { products } = await dispatch(
      getProductsUniversal({ productIDs: productIds }),
    );
    log('Received products', products);
    const groupIds = products
      .map(product => product.groupID)
      .filter(Boolean)
      .map(String);
    log('Returning group ids', groupIds);
    return new Set(groupIds);
  };
}

export const addProductGroupFooters: OnGenerateReceipt['on'] = (
  p,
  ap,
) => async (dispatch, getState) => {
  log('Getting configured receipt footers');
  const { receiptFooters } = getTgfConfiguration(getState());
  log('Got receipt footers', receiptFooters);
  if (!receiptFooters?.length) {
    return ap;
  }

  try {
    log('Getting product group ids from sale document');
    const productGroupIds = await dispatch(getProductGroupIds(p.salesDocument));
    log('Got product group ids from sale document', productGroupIds);
    if (!productGroupIds.size) {
      return ap;
    }

    log('Adding footers to receipt based on product groups');
    const footersAsPatchscript = R.pipe(
      Array.from,
      R.map(productGroupId =>
        receiptFooters.find(footer =>
          footer.productGroupIds.includes(productGroupId),
        ),
      ),
      R.filter(Boolean),
      R.uniq,
      R.pluck('footerText'),
      R.map(convertTextToPatchscript),
      R.intersperse(emptyTextLine),
    )(productGroupIds);

    const patchScript = await ap.patchScript;
    if (!patchScript) {
      return ap;
    }
    const receiptWithFooters = patchScript.concat(footersAsPatchscript);
    log('Appended footers to receipt. Final receipt:', receiptWithFooters);
    return {
      ...ap,
      patchScript: Promise.resolve(receiptWithFooters),
      // Prevent receipt-printout-api passing patchscript it generates directly to printing MS
      // because that patchscript does not contain product group footers
      saleDocumentId: Promise.resolve(undefined),
    };
  } catch (error) {
    // If something breaks do not let error propagate and cancel receipt generation
    // Let POS generate receipt without custom footers
    console.error('Failed to add custom receipt footers.', error);
    dispatch(addError('TGF plugin: Failed to add custom receipt footers'));
    return ap;
  }
};
